/**
 * fs-np-hero.js
 * National Park (fs-np) hero KV cross-fade.
 *
 * Each "set" (framed print + scenery split) is one Swiper slide; sets
 * cross-fade on a timer via Swiper's fade effect. Re-initializes in the
 * Shopify theme editor so merchant set add/remove/reorder is reflected.
 *
 * Vanilla JS, jQuery-independent. Degrades gracefully: when Swiper is
 * unavailable the first set stays visible (CSS hides non-first slides until
 * the container gains `.swiper-initialized`).
 */
(function () {
  'use strict';

  var HERO_SELECTOR = '[data-fs-np-hero]';
  var SWIPER_SELECTOR = '.fs-np-hero__images.swiper';
  var INSTANCE_KEY = 'fsNpHeroSwiper';
  var DEFAULT_INTERVAL = 6000;
  var FADE_SPEED = 1200;

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function initHero(hero) {
    if (!hero || hero[INSTANCE_KEY]) return; // already initialized
    if (typeof window.Swiper !== 'function') return; // graceful fallback

    var el = hero.querySelector(SWIPER_SELECTOR);
    if (!el) return;

    var slideCount = el.querySelectorAll('.swiper-slide').length;
    if (slideCount < 1) return;

    var interval = parseInt(hero.getAttribute('data-interval'), 10);
    if (!interval || interval < 1000) interval = DEFAULT_INTERVAL;

    var multiple = slideCount > 1;
    var animate = multiple && !prefersReducedMotion();

    var instance = new window.Swiper(el, {
      effect: 'fade',
      fadeEffect: { crossFade: true },
      // rewind (not loop): loop needs a duplicate buffer it cannot build from
      // just two fade slides and wedges; rewind fades back to the first set
      // after the last, cycling cleanly for any set count >= 2.
      rewind: multiple,
      speed: FADE_SPEED,
      allowTouchMove: false, // background KV, not a swipe gallery
      autoHeight: true, // slide height differs by breakpoint (row vs stack)
      a11y: { enabled: true },
      autoplay: animate
        ? { delay: interval, disableOnInteraction: false }
        : false,
    });

    instance.fsAnimate = animate; // remember whether autoplay is in play
    hero[INSTANCE_KEY] = instance;
  }

  function destroyHero(hero) {
    if (hero && hero[INSTANCE_KEY]) {
      hero[INSTANCE_KEY].destroy(true, true);
      hero[INSTANCE_KEY] = null;
    }
  }

  function pauseHero(hero) {
    var sw = hero && hero[INSTANCE_KEY];
    if (sw && sw.fsAnimate && sw.autoplay) sw.autoplay.stop();
  }

  function resumeHero(hero) {
    var sw = hero && hero[INSTANCE_KEY];
    if (sw && sw.fsAnimate && sw.autoplay) sw.autoplay.start();
  }

  function forEachHero(root, callback) {
    if (!root) return;
    var scope = root.querySelectorAll ? root : document;
    var heroes = scope.querySelectorAll(HERO_SELECTOR);
    for (var i = 0; i < heroes.length; i++) callback(heroes[i]);
    // A section-load target may itself be the hero root.
    if (root.matches && root.matches(HERO_SELECTOR)) callback(root);
  }

  function initAll(root) {
    forEachHero(root || document, initHero);
  }

  // Script is deferred, so the DOM is parsed by the time this runs.
  initAll(document);

  // Shopify theme editor: rebuild when the section reloads (block add/remove/
  // reorder re-renders the whole section), and pause autoplay while a merchant
  // is focused on a specific set so it does not advance out from under them.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    forEachHero(event.target, destroyHero);
  });

  // Block events fire on the set element, which is a DESCENDANT of the hero, so
  // walk up with closest() rather than searching inside the target.
  document.addEventListener('shopify:block:select', function (event) {
    var hero = event.target.closest && event.target.closest(HERO_SELECTOR);
    if (!hero) return;
    pauseHero(hero); // don't advance out from under a merchant editing a set
    var sw = hero[INSTANCE_KEY];
    if (!sw || !sw.slides) return;
    var slide = event.target.closest('.swiper-slide');
    var idx = slide ? Array.prototype.indexOf.call(sw.slides, slide) : -1;
    if (idx >= 0 && typeof sw.slideTo === 'function') sw.slideTo(idx, 0);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var hero = event.target.closest && event.target.closest(HERO_SELECTOR);
    if (hero) resumeHero(hero);
  });

  // Recalculate on viewport changes. Swiper's built-in resize observer can miss
  // the 960px breakpoint flip (side-by-side ⇄ stacked) in some embeddings, which
  // leaves slides sized for the previous layout; an explicit update() is cheap
  // and idempotent. Debounce with setTimeout (not rAF, which is paused while the
  // page is hidden) so it still settles reliably.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      forEachHero(document, function (hero) {
        var sw = hero[INSTANCE_KEY];
        if (sw && typeof sw.update === 'function') sw.update();
      });
    }, 150);
  });
})();
